import { styled } from "../../../styles";

/** Styles specific to the Cognito Migration page (EN-5222). */

export const ProgressTrack = styled("div", {
  width: "100%",
  height: "8px",
  borderRadius: "4px",
  backgroundColor: "$gray-20",
  overflow: "hidden",
});

export const ProgressFill = styled("div", {
  height: "100%",
  borderRadius: "4px",
  backgroundColor: "$primary",
  transition: "width 0.2s ease",
});

export const RunActions = styled("div", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
  flexWrap: "wrap",
});

export const DetailCell = styled("span", {
  display: "inline-block",
  maxWidth: "360px",
  whiteSpace: "normal",
  fontSize: "13px",
  color: "$gray-80",
  fontFamily: "$openSans",
});

export const RunDialogBody = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "min(1100px, 92vw)",
  maxHeight: "calc(90vh - 48px)",
  overflowY: "auto",
});

/** The confirmation's two figures, side by side and large. */
export const ConfirmStats = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "12px",
  margin: "8px 0 4px",
});

export const ConfirmStatCard = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  padding: "18px 20px",
  borderRadius: "8px",
  border: "1px solid $gray-30",
  backgroundColor: "$white",

  variants: {
    tone: {
      primary: {
        borderColor: "$primary",
        backgroundColor: "$primary-20",
      },
      muted: {},
    },
  },
  defaultVariants: { tone: "muted" },
});

export const ConfirmStatValue = styled("span", {
  fontSize: "32px",
  lineHeight: 1,
  fontWeight: 700,
  fontFamily: "$openSans",
  color: "$charcoal",

  variants: {
    tone: {
      primary: { color: "$primary" },
      muted: { color: "$gray-80" },
    },
  },
  defaultVariants: { tone: "muted" },
});

export const ConfirmStatLabel = styled("span", {
  fontSize: "13px",
  fontWeight: 600,
  fontFamily: "$openSans",
  color: "$charcoal",
});

export const ConfirmStatHint = styled("span", {
  fontSize: "12px",
  fontFamily: "$openSans",
  color: "$gray-80",
});

/** Three short points instead of two paragraphs. */
export const ConfirmList = styled("ul", {
  margin: "4px 0 8px",
  paddingLeft: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  fontFamily: "$openSans",
  fontSize: "14px",
  lineHeight: 1.5,
  color: "$charcoal",
});

/** The close confirmation, styled as a warning so it reads as one at a glance. */
export const WarningDialogHeader = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "16px 24px",
  backgroundColor: "$warning-lightest",
  borderBottom: "1px solid $warning-light",
});

export const WarningDialogIcon = styled("span", {
  display: "inline-flex",
  flexShrink: 0,
  width: "28px",
  height: "28px",
  color: "$warning-dark",

  "& svg": {
    width: "100%",
    height: "100%",
  },
});

export const WarningDialogTitle = styled("h2", {
  margin: 0,
  fontFamily: "$openSans",
  fontSize: "18px",
  fontWeight: 700,
  color: "$warning-darkest",
});

export const WarningDialogBody = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  padding: "20px 24px 24px",
});

/**
 * The confirmation's actions: the safe way out on the left, the two ways of
 * leaving on the right, and no label ever wrapping onto a second line.
 */
export const WarningDialogActions = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  marginTop: "4px",

  "& button": {
    whiteSpace: "nowrap",
  },
});

export const WarningDialogActionsGroup = styled("div", {
  display: "flex",
  gap: "8px",
  marginLeft: "auto",
});

/** Positions the fetching overlay over the rows. */
export const TableStage = styled("div", {
  position: "relative",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
});

/**
 * Shown over the previous page's rows while the next one loads. Every row on
 * this list costs the API a Cognito lookup, so a page change takes seconds,
 * and the kept-previous-data pattern the other admin tables use would leave
 * the old rows on screen with no sign that anything is happening.
 */
export const TableFetchingOverlay = styled("div", {
  position: "absolute",
  inset: 0,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.72)",
  cursor: "progress",
});
